import CentralizedModalContainer from "@/appComponents/Blocks/Utils/CentralizedModalContainer/CentralizedModalContainer";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Flex,
  Icon,
  Select,
  Modal,
  Text,
  Divider
} from "@theorgchartdev/storybook";
import React, { useState } from "react";
import cx from "classnames";
import EditableItemGroup from "@/appComponents/Blocks/Utils/EditableItemGroup/EditableItemGroup";
import { FieldValueGroup } from "./FieldValueGroup";
import { GetDoc } from "@/Utils/StateLib";
import { GetRulePropertyString } from "@/appComponents/Blocks/Modals/SettingsModal/AccountSettings/Security/SecurityUtils";
import { kRowSecDefintionList } from "@/appComponents/Blocks/Modals/SettingsModal/AccountSettings/Security/SecurityUtils";
import { appstr } from "@coreapp/utils/appstr";
import { enumObjClassNames } from "@coreapp/constants";

const initializeRule = ruleObj => {
  const availableRuleTypes = Object.values(kRowSecDefintionList);

  return {
    ...ruleObj,
    properties: availableRuleTypes.includes(ruleObj.properties) ? ruleObj.properties : "$eq",
    condRuleOperator: ruleObj.condRuleOperator ?? 0,
    definition: ruleObj.definition || {},
    pos_definition: ruleObj.pos_definition || {}
  };
};

export const ConditionalDefinitionModal = ({
  show,
  onSave,
  onClose,
  ruleObj,
  index,
  fieldDefs
}) => {
  if (!show) return;

  const isNewDefinition = index == null || index === -1;

  const [rule, setRule] = useState(initializeRule(ruleObj));
  const [ruleIndex, setRuleIndex] = useState(index);
  const [fieldOrder, setFieldOrder] = useState(() => {
    const order = [];
    Object.keys(rule.definition || {}).forEach(field => {
      order.push({ field, className: enumObjClassNames.box });
    });
    Object.keys(rule.pos_definition || {}).forEach(field => {
      order.push({ field, className: enumObjClassNames.position });
    });
    return order;
  });

  const isIncludesType = rule["properties"] === "$in" || rule["properties"] === "$nin";

  const onSaveDefinition = () => {
    onSave(rule, ruleIndex);
  };

  const GetDefKey = className => {
    return className === enumObjClassNames.box ? "definition" : "pos_definition";
  };

  const handleUpdate = (key, value) => {
    setRule(prevRule => ({ ...prevRule, [key]: value }));
  };

  const onRuleTypeChange = o => {
    let newType = o.value;
    let ruleClone = JSON.parse(JSON.stringify(rule));
    var curType = ruleClone["properties"];
    var isIncludesType = curType == "$in" || curType == "$nin";

    if (isIncludesType && newType !== "$in" && newType !== "$nin") {
      // make sure to clear here so we don't carry over potentially invalid data structure
      handleUpdate("definition", {});
      handleUpdate("pos_definition", {});
      setFieldOrder([]);
    }
    handleUpdate("properties", o.value);
  };

  const BuildRulePropertyTypes = () => {
    let rulePropTypeOptions = [];
    for (var aDefinitionIdx in kRowSecDefintionList) {
      let option = {};
      option.label = appstr(aDefinitionIdx);
      option.value = kRowSecDefintionList[aDefinitionIdx];
      option.clickHandler = onRuleTypeChange;
      option.dataTestId = `rls-value-comparison-${aDefinitionIdx}`;
      rulePropTypeOptions.push(option);
    }
    return rulePropTypeOptions;
  };

  const updateOpLogic = o => {
    handleUpdate("condRuleOperator", o.value);
  };
  const BuildLogicOptions = () => {
    let options = [];

    let orOption = {};
    orOption.label = "OR";
    orOption.value = 0;
    orOption.clickHandler = updateOpLogic;
    orOption.dataTestId = "account-settings-security-conditional-rule-comparison-or-option";
    options.push(orOption);

    let andOption = {};
    andOption.label = "AND";
    andOption.value = 1;
    andOption.clickHandler = updateOpLogic;
    andOption.dataTestId = "account-settings-security-conditional-rule-comparison-and-option";
    options.push(andOption);

    return options;
  };

  const GetDefCopy = className => {
    const definitionKey = GetDefKey(className);
    return JSON.parse(JSON.stringify(rule[definitionKey]));
  };

  const AddFieldValue = () => {
    let definitionClone = GetDefCopy(enumObjClassNames.box);
    definitionClone[""] = "";
    handleUpdate("definition", definitionClone);
    setFieldOrder(prev => [...prev, { field: "", className: enumObjClassNames.box }]);
  };

  const AppendFieldListValue = (field, className) => {
    const definitionKey = GetDefKey(className);
    const definitionClone = GetDefCopy(className);
    let valueArray = Array.isArray(definitionClone[field])
      ? [...definitionClone[field]]
      : definitionClone[field] !== undefined && definitionClone[field] !== ""
        ? [definitionClone[field]]
        : [];
    valueArray.push("");
    definitionClone[field] = valueArray;
    handleUpdate(definitionKey, definitionClone);
  };

  const RemoveFieldValue = (field, className) => {
    let definitionClone = GetDefCopy(className);
    delete definitionClone[field];
    handleUpdate(GetDefKey(className), definitionClone);
    setFieldOrder(prev =>
      prev.filter(item => !(item.field === field && item.className === className))
    );
  };

  const RemoveFieldValueListItem = (field, index, className) => {
    if (index == -1 || index == null) return;
    let definitionClone = GetDefCopy(className);
    if (definitionClone[field].length < 2) return; // prevent removing last item
    definitionClone[field].splice(index, 1);
    handleUpdate(GetDefKey(className), definitionClone);
  };

  const UpdateFieldValue = ({ field, value, index }, className) => {
    let type = rule["properties"];
    let definitionClone = GetDefCopy(className);
    if ((type === "$in" || type === "$nin") && Array.isArray(definitionClone[field])) {
      if (index != null && index != "null") definitionClone[field][index] = value;
      else {
        definitionClone[field].push(value);
      }
    } else definitionClone[field] = value;
    handleUpdate(GetDefKey(className), definitionClone);
  };

  const getCurrentFieldClass = field => {
    if (rule.definition && rule.definition.hasOwnProperty(field)) {
      return enumObjClassNames.box;
    }
    if (rule.pos_definition && rule.pos_definition.hasOwnProperty(field)) {
      return enumObjClassNames.position;
    }
    return enumObjClassNames.box;
  };

  const ChangeField = (oldField, newField, targetClassName) => {
    const currentClassName = getCurrentFieldClass(oldField);

    setRule(prevRule => {
      const currentDefKey = GetDefKey(currentClassName);
      const targetDefKey = GetDefKey(targetClassName);

      const currentDef = { ...prevRule[currentDefKey] };
      const targetDef =
        targetClassName === currentClassName ? currentDef : { ...prevRule[targetDefKey] };

      delete currentDef[oldField];

      if (newField === "_branchlevel") {
        targetDef[newField] = "self+subordinates";
      } else {
        targetDef[newField] = "";
      }

      if (currentClassName !== targetClassName) {
        return {
          ...prevRule,
          [currentDefKey]: currentDef,
          [targetDefKey]: targetDef
        };
      } else {
        return {
          ...prevRule,
          [currentDefKey]: targetDef
        };
      }
    });

    setFieldOrder(prev =>
      prev.map(item => {
        if (item.field === oldField && item.className === currentClassName) {
          return { field: newField, className: targetClassName };
        }
        return item;
      })
    );
  };

  const GetFieldOptionsFromList = (fields, clickHandler, className) => {
    let options = [];
    fields.forEach(field => {
      let option = {};
      option.label = field;
      option.value = `${className}::${field}`;
      option.clickHandler = () => clickHandler(field, className);
      option.dataTestId = `rls-field-option-${field}`;
      options.push(option);
    });
    return options;
  };

  const BuildFieldOptions = curField => {
    const definitionClone = GetDefCopy(enumObjClassNames.box);
    const positionDefinitionClone = GetDefCopy(enumObjClassNames.position);
    let fieldOptions = [];
    const empExcludeFields = Object.keys(definitionClone).filter(item => item != curField);
    const posExcludeFields = Object.keys(positionDefinitionClone).filter(item => item != curField);
    const curDoc = GetDoc();
    if (!curDoc) return fieldOptions;

    let addBranchLevelOption =
      curField === "_branchlevel" || empExcludeFields.indexOf("_branchlevel") === -1;
    if (addBranchLevelOption) {
      let branchOptionOn = {};
      branchOptionOn.label = appstr("Branch Level");
      branchOptionOn.value = "_branchlevel";
      branchOptionOn.clickHandler = () =>
        ChangeField(curField, "_branchlevel", enumObjClassNames.box);
      fieldOptions.push(branchOptionOn);
    }

    if (curDoc.IsPositionDocument()) {
      const employeeFields = fieldDefs.Box.map(field => field.GetFieldName()).filter(
        field => !empExcludeFields.includes(field)
      );
      const positionFields = fieldDefs.Position.map(field => field.GetFieldName()).filter(
        field => !posExcludeFields.includes(field)
      );

      fieldOptions.push({
        label: enumObjClassNames.box,
        value: `${enumObjClassNames.box}-header`,
        content: (
          <Text size="small" modifier="medium">
            {appstr("Employee fields")}
          </Text>
        ),
        clickHandler: () => {}
      });

      fieldOptions.push(
        ...GetFieldOptionsFromList(
          employeeFields,
          field => ChangeField(curField, field, enumObjClassNames.box),
          enumObjClassNames.box
        )
      );

      fieldOptions.push({
        label: enumObjClassNames.position,
        value: `${enumObjClassNames.position}-header`,
        content: (
          <Text size="small" modifier="medium">
            {appstr("Position fields")}
          </Text>
        ),
        clickHandler: () => {}
      });

      fieldOptions.push(
        ...GetFieldOptionsFromList(
          positionFields,
          field => ChangeField(curField, field, enumObjClassNames.position),
          enumObjClassNames.position
        )
      );
    } else {
      const headers = curDoc.GetTargetHeaders(true, false, true, true, empExcludeFields);
      fieldOptions.push(
        ...GetFieldOptionsFromList(
          headers,
          field => ChangeField(curField, field, enumObjClassNames.box),
          enumObjClassNames.box
        )
      );
    }

    return fieldOptions;
  };

  const GetFieldValueSelect = (field, value, index, className) => {
    if (typeof value === "string" && isIncludesType) {
      const definition = rule[GetDefKey(className)];
      // preserve backward compatibility before these were proper list value structures
      value = definition[field] ? definition[field].split(",") : [""];
    }

    return (
      <FieldValueGroup
        index={index}
        field={field}
        value={value}
        className={className}
        onChange={({ field, value, index }) => UpdateFieldValue({ field, value, index }, className)}
        onRemove={(field, indexToRemove) =>
          RemoveFieldValueListItem(field, indexToRemove, className)
        }
        fieldOptions={BuildFieldOptions(field)}
        dataTestIdPrefix={`rls-field-${index}-`}
      />
    );
  };

  const GetAllFieldEntries = () => {
    const allEntries = [];

    // Use field order to maintain visual consistency
    fieldOrder.forEach((orderItem, index) => {
      const { field, className } = orderItem;
      const defKey = GetDefKey(className);
      const definition = rule[defKey];

      // Only include fields that actually exist in the definition
      if (definition && definition.hasOwnProperty(field)) {
        allEntries.push({
          field,
          value: definition[field],
          className,
          key: `${className}-${field}-${index}`
        });
      }
    });

    //if fieldOrder is empty or doesn't match definitions, rebuild it
    if (allEntries.length === 0) {
      const newOrder = [];

      Object.entries(rule.definition || {}).forEach(([field, value], entryIndex) => {
        allEntries.push({
          field,
          value,
          className: enumObjClassNames.box,
          key: `box-${field}-${entryIndex}`
        });
        newOrder.push({ field, className: enumObjClassNames.box });
      });

      Object.entries(rule.pos_definition || {}).forEach(([field, value], entryIndex) => {
        allEntries.push({
          field,
          value,
          className: enumObjClassNames.position,
          key: `pos-${field}-${entryIndex}`
        });
        newOrder.push({ field, className: enumObjClassNames.position });
      });

      if (newOrder.length > 0) {
        setFieldOrder(newOrder);
      }
    }

    return allEntries;
  };

  const definition = rule["definition"];
  const positionDefinition = rule["pos_definition"] ?? {};
  const combinedDefinition = GetAllFieldEntries();
  const property = rule["properties"];
  const { aboutString } = GetRulePropertyString(property);
  const id = ruleIndex > -1 ? parseInt(ruleIndex) + 1 : 1;

  return (
    <CentralizedModalContainer show={show}>
      <Modal
        id="example-modal"
        show={show}
        minWidth={"medium"}
        maxWidth={"medium"}
        fullWidth={true}
        centerScreen={true}
      >
        <DialogTitle
          divider={true}
          onClose={() => onClose()}
          data-testid="account-settings-security-conditional-modal-close-btn"
        >
          <span className={"ocn-explorer-icon-label"}>
            <Icon size={"medium"} name={ruleIndex != null ? "pen" : "plus"} />
            {appstr("Conditional Definition") + " " + id.toString()}
          </span>
        </DialogTitle>
        <DialogContent customContentClass={"oc-modal-content-container"}>
          <Flex direction={"column"} gap={"12px"} height={"100%"} className={"oc-modal-content"}>
            <Flex gap={"4px"} padding={"0 0 8px 0"}>
              <Icon name={"circle-info"} size={"small"} />
              <Text size={"small"}>{aboutString}</Text>
            </Flex>
            <Flex gap={"20px"} width={"100%"}>
              <div style={{ width: "100%" }}>
                <Select
                  label={appstr("Rule comparison")}
                  inputOnly={false}
                  options={BuildLogicOptions()}
                  value={rule?.condRuleOperator ?? 0}
                  placeholder={appstr("Select comparison")}
                  popoverProps={{
                    flipOptions: {
                      mainAxis: false
                    }
                  }}
                  dataTestId="account-settings-security-conditional-comparison-select"
                />
              </div>
              <div style={{ width: "100%" }}>
                <Select
                  label={appstr("Value comparison")}
                  inputOnly={false}
                  options={BuildRulePropertyTypes()}
                  value={rule?.properties}
                  placeholder={appstr("Select comparison")}
                  popoverProps={{
                    flipOptions: {
                      mainAxis: false
                    }
                  }}
                  dataTestId="rls-value-comparison-select"
                />
              </div>
            </Flex>
            <Divider vertical={false} />
            <Flex
              direction={"column"}
              gap={"8px"}
              width={"100%"}
              height={"100%"}
              padding={"4px 0 0 0"}
            >
              <Flex
                justifyContent={"space-between"}
                gap={"4px"}
                alignItems={"center"}
                width={"100%"}
              >
                <Text modifier={"bold"} size={"small"}>
                  {appstr("Field value criteria")}
                </Text>
                <Flex className={"oc-right-justify"}>
                  <Button
                    size={"small"}
                    className={cx("oc-special-secondary", "oc-special-secondary--fit")}
                    modifier={"secondary"}
                    iconProps={{ name: "plus-solid" }}
                    onClick={AddFieldValue}
                    tooltipProps={{ message: appstr("Add field value to rule criteria") }}
                    dataTestId="rls-add-field-button"
                  >
                    {appstr("Add field")}
                  </Button>
                </Flex>
              </Flex>
              <Flex
                direction={"column"}
                className={cx("oc-list-container", "ocn-list-scrollbar")}
                gap={"20px"}
                justifyContent={"flex-start"}
                width={"100%"}
                padding={"20px 0 0 0"}
              >
                {combinedDefinition &&
                  combinedDefinition.map(({ field, value, className, key }, index) => (
                    <Flex direction={"column"} gap={"4px"} key={key}>
                      <Flex alignItems={"flex-start"} gap={"4px"}>
                        <EditableItemGroup
                          centerActions={false}
                          key={key}
                          actions={[
                            {
                              iconName: "trash-can",
                              iconProps: { color: "error" },
                              tooltipProps: {
                                message: appstr("Remove field value from rule criteria")
                              },
                              onClick: () => {
                                RemoveFieldValue(field, className);
                              },
                              dataTestId: "account-settings-security-conditional-field-remove-btn"
                            }
                          ]}
                          buttonsInHierarchy
                        >
                          {GetFieldValueSelect(field, value, index, className)}
                        </EditableItemGroup>
                        {isIncludesType && field !== "_branchlevel" && (
                          <Button
                            size={"small"}
                            style={{ width: "35px" }}
                            className={cx("oc-special-secondary", "oc-special-secondary--fit")}
                            modifier={"secondary"}
                            iconOnly={true}
                            disabled={field == "" || field == null}
                            isFullWidth={false}
                            iconProps={{ name: "plus" }}
                            onClick={() => AppendFieldListValue(field, className)}
                            tooltipProps={{ message: appstr("Add value to field") }}
                            dataTestId={`rls-add-field-value-button-${index}`}
                          />
                        )}
                      </Flex>
                    </Flex>
                  ))}
              </Flex>
            </Flex>
          </Flex>
        </DialogContent>
        <DialogActions divider={true}>
          <DialogActions.Tertiary
            onClick={() => onClose()}
            dataTestId="account-settings-security-conditional-modal-cancel-btn"
          >
            {appstr("Cancel")}
          </DialogActions.Tertiary>
          <DialogActions.Primary onClick={onSaveDefinition} dataTestId="rls-save-definition-button">
            {isNewDefinition ? appstr("Create") : appstr("Save")}
          </DialogActions.Primary>
        </DialogActions>
      </Modal>
    </CentralizedModalContainer>
  );
};
